import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import HomeView from '../home/HomeView';
import MenuView from '../menu/MenuView';
import MapView from '../media/maps/MapView';
import FooterBar from '../footer/FooterBar';
import Popup from '../popup/Popup';
import MediaView from '../media/MediaView';
import TableOfContents from '../toc/TableOfContents';
import HelpPopup from '../popup/HelpPopup';
import DevTools from '../dev/DevTools';
import TimerPopup from '../popup/TimerPopup';
import Background from '../home/Background';
import DataModel from '../../models/dataModel';
import Logger from '../../helpers/logger';
import Focus from '../../helpers/focus';
import VideoHelper from '../../helpers/videoHelper';
import OrientationPrompt from '../common/OrientationPrompt';

function handleScroll() {
	const view = document.getElementById('view');
	view.scrollTop = 0;
}

const isBrowser = typeof window !== 'undefined' && !!window.history && !!window.history.pushState;
const buildHistoryKey = (view, media) => `${view}|${media || ''}`;
const buildHistoryState = (view, media) => ({ app: 'kiosk', view, media: media || '' });

const ViewManager = React.memo(({ state, actions }) => {
	const { activeBackgroundID, activeViewID, activeMediaID, activePopupID, timer, activeVolume, webMode, transcript } = state;
	const {
		setBackground,
		setView,
		setPopup,
		setCarousel,
		setTimer,
		resetTimer,
		setConnectionStatus,
		stopSound,
		playVideo,
		stopVideo,
		playSound,
	} = actions;

	const latestStateRef = useRef({ activeViewID, activeMediaID, activeVolume });
	const actionsRef = useRef(actions);
	const historyInitializedRef = useRef(false);
	const handlingPopstateRef = useRef(false);
	const historyKeyRef = useRef(buildHistoryKey(activeViewID, activeMediaID));
	const [showOrientationPrompt, setShowOrientationPrompt] = useState(false);

	useEffect(() => {
		const timeout = DataModel.getSettingsAttribute('timeout');
		const timeoutLong = DataModel.getSettingsAttribute('timeoutLong');
		setTimer(timeout, timeout, timeoutLong);
	}, [setTimer]);

	useEffect(() => {
		latestStateRef.current = { activeViewID, activeMediaID, activeVolume };
	}, [activeViewID, activeMediaID, activeVolume]);

	useEffect(() => {
		actionsRef.current = actions;
	}, [actions]);

	useEffect(() => {
		if (!isBrowser) {
			return undefined;
		}

		const orientationQuery = window.matchMedia('(orientation: portrait)');
		const mobileQuery = window.matchMedia('(max-width: 900px)');
		const updatePrompt = () => {
			setShowOrientationPrompt(orientationQuery.matches && mobileQuery.matches);
		};
		updatePrompt();

		const subscribe = (query) => {
			if (typeof query.addEventListener === 'function') {
				query.addEventListener('change', updatePrompt);
				return () => query.removeEventListener('change', updatePrompt);
			}
			query.addListener(updatePrompt);
			return () => query.removeListener(updatePrompt);
		};

		const cleanupOrientation = subscribe(orientationQuery);
		const cleanupMobile = subscribe(mobileQuery);
		window.addEventListener('resize', updatePrompt);

		return () => {
			cleanupOrientation();
			cleanupMobile();
			window.removeEventListener('resize', updatePrompt);
		};
	}, []);

	useEffect(() => {
		if (!isBrowser) {
			return undefined;
		}

		const handlePopState = (event) => {
			if (!event.state || event.state.app !== 'kiosk') {
				return;
			}
			handlingPopstateRef.current = true;
			const { view: targetView, media: targetMedia } = event.state;
			const { activeViewID: currentView, activeMediaID: currentMedia, activeVolume: currentVolume } = latestStateRef.current;
			const currentActions = actionsRef.current;

			if (targetView === 'menu') {
				currentActions.setView('menu');
				Focus.set('menu');
				const videoBackground = DataModel.getHomeAttribute('backgroundVideo', true);
				if (!videoBackground) {
					currentActions.playSound('intro', currentVolume);
				}
				VideoHelper.fadeVolume(currentVolume);
				currentActions.stopVideo();
				Logger.log('open-screen', 'main-menu', 'Main Menu');
			} else if (targetView === 'map') {
				if (currentView !== 'map') {
					currentActions.setView('map');
				}
				if (targetMedia && targetMedia !== currentMedia) {
					currentActions.setMedia(targetMedia);
					currentActions.stopVideo('video');
					const title = DataModel.getMediaTitle(targetMedia);
					if (title) {
						Logger.log('open-media', 'map', title);
					}
				}
				if (!targetMedia) {
					currentActions.setMedia('');
				}
				Focus.set('map');
			} else if (targetView === 'home') {
				currentActions.setView('home');
				Focus.set('home');
				VideoHelper.fadeVolume(currentVolume);
			} else {
				if (targetView && targetView !== currentView) {
					currentActions.setView(targetView);
				}
				if (typeof targetMedia !== 'undefined' && targetMedia !== currentMedia) {
					currentActions.setMedia(targetMedia);
				}
			}

			historyKeyRef.current = buildHistoryKey(targetView, targetMedia);
			const finalizeHandling = () => {
				handlingPopstateRef.current = false;
			};
			if (typeof window.requestAnimationFrame === 'function') {
				window.requestAnimationFrame(finalizeHandling);
			} else {
				setTimeout(finalizeHandling, 0);
			}
		};

		window.addEventListener('popstate', handlePopState);
		return () => {
			window.removeEventListener('popstate', handlePopState);
		};
	}, []);

	useEffect(() => {
		if (!isBrowser) {
			return;
		}
		const key = buildHistoryKey(activeViewID, activeMediaID);
		if (!historyInitializedRef.current) {
			window.history.replaceState(buildHistoryState(activeViewID, activeMediaID), '', window.location.href);
			historyKeyRef.current = key;
			historyInitializedRef.current = true;
			return;
		}
		if (handlingPopstateRef.current) {
			historyKeyRef.current = key;
			return;
		}
		if (historyKeyRef.current === key) {
			return;
		}
		if (activeViewID === 'map' && !activeMediaID) {
			historyKeyRef.current = key;
			return;
		}
		window.history.pushState(buildHistoryState(activeViewID, activeMediaID), '', window.location.href);
		historyKeyRef.current = key;
	}, [activeViewID, activeMediaID]);

	const handleMobileMenuClick = useCallback(() => {
		if (activeViewID === 'menu' && !activeMediaID && !activePopupID) {
			return;
		}
		if (activePopupID) {
			setPopup('');
		}
		setView('menu');
		Focus.set('menu');
		const videoBackground = DataModel.getHomeAttribute('backgroundVideo', true);
		if (!videoBackground) {
			playSound('intro', activeVolume);
		}
		VideoHelper.fadeVolume(activeVolume);
		stopVideo();
		Logger.log('open-screen', 'main-menu', 'Main Menu');
	}, [activeMediaID, activePopupID, activeViewID, activeVolume, playSound, setPopup, setView, stopVideo]);

	const hasMenuOverlay = activeViewID === 'menu' && (activeMediaID || activePopupID);
	const showMobileMenuButton = activeViewID !== 'menu' || hasMenuOverlay;

	useEffect(() => {
		if (!window.ipcRenderer) {
			return undefined;
		}
		const helpFileType = DataModel.getHelpFileType();
		const handleShowHelp = () => {
			setPopup('help');
			stopSound('intro');
			stopVideo();
			if (helpFileType === 'mp4') {
				playVideo();
			}
			Focus.set('help');
			Logger.log('open-popup', 'help', 'Help');
		};
		window.ipcRenderer.on('show-help-popup', handleShowHelp);
		return () => {
			window.ipcRenderer.removeListener('show-help-popup', handleShowHelp);
		};
	}, [setPopup, stopSound, stopVideo, playVideo]);

	return (
		<div className="view" id="view" onScroll={() => handleScroll()}>
			<OrientationPrompt visible={showOrientationPrompt} />
			<div className="view-overlays">
				<button
					type="button"
					className={`mobile-menu-button${showMobileMenuButton ? '' : ' mobile-menu-button-hidden'}`}
					onClick={handleMobileMenuClick}
					aria-hidden={!showMobileMenuButton}
					tabIndex={showMobileMenuButton ? '0' : '-1'}>
					<i className="fas fa-home" aria-hidden="true" />
					<span>Main Menu</span>
				</button>
			</div>
			<Background activeBackgroundID={activeBackgroundID} activeVolume={activeVolume} setBackground={setBackground} />
			<MenuView state={state} actions={actions} />
			<HomeView state={state} actions={actions} />
			<MapView state={state} actions={actions} />
			<FooterBar state={state} actions={actions} />
			<MediaView state={state} actions={actions} />
			<TableOfContents state={state} actions={actions} />
			<HelpPopup state={state} actions={actions} />
			<Popup
				activeViewID={activeViewID}
				activeMediaID={activeMediaID}
				activePopupID={activePopupID}
				transcript={transcript}
				setPopup={setPopup}
			/>
			<TimerPopup
				activeViewID={activeViewID}
				timer={timer}
				webMode={webMode}
				setView={setView}
				setPopup={setPopup}
				setCarousel={setCarousel}
				setTimer={setTimer}
				resetTimer={resetTimer}
				setConnectionStatus={setConnectionStatus}
			/>
			<DevTools state={state} actions={actions} />
		</div>
	);
});

ViewManager.propTypes = {
	state: PropTypes.object.isRequired,
	actions: PropTypes.object.isRequired,
};

export default ViewManager;
